import { useEffect, useState } from 'react';

/**
 * Compact poster thumbnail with click-to-enlarge overlay. Used on atelier /
 * masterclass / conférence cards to show the affiche image in-line rather
 * than as a plain "Voir l'affiche" link.
 *
 * - Renders nothing when `url` is null/empty
 * - Thumbnail aspect ratio 3/4 (portrait poster default), object-cover
 * - Click → full-screen lightbox overlay with the original image, close on
 *   backdrop click, × button, or Escape.
 */
export function AfficheThumbnail({
  url,
  alt,
  width = 96,
}: {
  url: string | null | undefined;
  alt: string;
  width?: number;
}) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!url) return null;

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        aria-label={`Agrandir l'affiche : ${alt}`}
        style={{
          display: 'block',
          flexShrink: 0,
          width,
          aspectRatio: '3 / 4',
          border: 0,
          padding: 0,
          background: '#f2efe7',
          borderRadius: 8,
          overflow: 'hidden',
          cursor: 'zoom-in',
          boxShadow: '0 2px 8px rgba(15,25,22,0.12)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(15,25,22,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(15,25,22,0.12)';
        }}
      >
        <img
          src={url}
          alt={alt}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Affiche : ${alt}`}
          onClick={() => setOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1100,
            background: 'rgba(15,25,22,0.85)',
            backdropFilter: 'blur(6px)',
            display: 'grid',
            placeItems: 'center',
            padding: 24,
            animation: 'mtcFadeIn 0.18s ease',
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
            aria-label="Fermer"
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              width: 44,
              height: 44,
              borderRadius: 999,
              border: 0,
              background: 'rgba(255,255,255,0.18)',
              color: 'white',
              fontSize: 22,
              lineHeight: 1,
              cursor: 'pointer',
              backdropFilter: 'blur(8px)',
              transition: 'background 0.18s ease',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.28)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.18)')}
          >
            ×
          </button>
          <img
            src={url}
            alt={alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '100%',
              maxHeight: 'calc(100vh - 48px)',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
              animation: 'mtcZoomIn 0.22s cubic-bezier(0.2, 0.9, 0.3, 1.1)',
            }}
          />
          <style>{`
            @keyframes mtcFadeIn { from { opacity: 0 } to { opacity: 1 } }
            @keyframes mtcZoomIn {
              from { opacity: 0; transform: scale(0.94) }
              to { opacity: 1; transform: scale(1) }
            }
          `}</style>
        </div>
      )}
    </>
  );
}
